export type CoreDomain = "CONNECT" | "OWN" | "RESPOND" | "ELEVATE";

export type ObservationPolarity = "supporting" | "opposing";

export type ConstellationNodeState =
  | "dormant"
  | "emerging"
  | "strengthening"
  | "mixed"
  | "receding";

export interface ConstellationEvidenceItem {
  meetingId: string;
  meetingTitle: string;
  meetingStartTime: string;
  polarity: ObservationPolarity;
  sourceType: "transcript" | "context_graph" | "behavioral_intelligence";
  excerpt: string | null;
  confidence: "low" | "medium" | "high";
  contextTags: string[];
}

export interface BehaviorConstellationState {
  behaviorId: string;
  behaviorName: string;
  theme: CoreDomain;
  supportScore: number;
  oppositionScore: number;
  netIntensity: number;
  state: ConstellationNodeState;
  supportingMeetingCount30d: number;
  opposingMeetingCount30d: number;
  lastSupportedAt: string | null;
  lastOpposedAt: string | null;
  evidenceMeetingIds: string[];
  contextTags: string[];
  recentEvidence: ConstellationEvidenceItem[];
  phase: string | null;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function inferConstellationNodeState(params: {
  supportingCount: number;
  opposingCount: number;
  hasPriorPositivePattern: boolean;
}): ConstellationNodeState {
  const { supportingCount, opposingCount, hasPriorPositivePattern } = params;

  if (supportingCount === 0 && opposingCount === 0) return "dormant";
  if (opposingCount >= 2 && hasPriorPositivePattern && opposingCount >= supportingCount) {
    return "receding";
  }
  if (supportingCount >= 3 && opposingCount === 0) return "strengthening";
  if (supportingCount >= 2 && opposingCount >= 1) return "mixed";
  if (supportingCount >= 1) return "emerging";
  if (opposingCount >= 1) return "mixed";
  return "dormant";
}

export function computeNetIntensity(params: {
  supportScore: number;
  oppositionScore: number;
  state: ConstellationNodeState;
}): number {
  const { supportScore, oppositionScore, state } = params;
  const baseline = clamp((supportScore * 0.85) - (oppositionScore * 0.55), 0, 1);

  if (state === "strengthening") return clamp(baseline + 0.2, 0.15, 1);
  if (state === "emerging") return clamp(baseline + 0.1, 0.1, 0.7);
  if (state === "mixed") return clamp(baseline, 0.15, 0.65);
  if (state === "receding") return clamp(baseline * 0.55, 0.08, 0.45);
  return clamp(baseline * 0.35, 0.04, 0.25);
}

export function getNodeVisualSpec(state: ConstellationNodeState, intensity: number) {
  const clamped = clamp(intensity, 0, 1);

  if (state === "dormant") {
    return {
      outerClass: "border-border/60 bg-background/70",
      innerClass: "bg-transparent",
      auraOpacity: 0.06,
      saturation: 0.6,
    };
  }

  if (state === "emerging") {
    return {
      outerClass: "border-primary/60 bg-primary/[0.10]",
      innerClass: "bg-primary/55",
      auraOpacity: clamp(0.15 + (clamped * 0.2), 0.15, 0.35),
      saturation: 0.85,
    };
  }

  if (state === "strengthening") {
    return {
      outerClass: "border-primary bg-primary/[0.20]",
      innerClass: "bg-primary/80",
      auraOpacity: clamp(0.3 + (clamped * 0.25), 0.3, 0.65),
      saturation: 1,
    };
  }

  if (state === "mixed") {
    return {
      outerClass: "border-primary/70 bg-gradient-to-br from-primary/20 via-primary/10 to-muted/30",
      innerClass: "bg-primary/60",
      auraOpacity: clamp(0.18 + (clamped * 0.18), 0.18, 0.4),
      saturation: 0.8,
    };
  }

  return {
    outerClass: "border-primary/40 bg-primary/[0.08] opacity-85",
    innerClass: "bg-primary/35",
    auraOpacity: clamp(0.08 + (clamped * 0.12), 0.08, 0.25),
    saturation: 0.6,
  };
}
